import { useStore } from '../../store';
import { useToast } from './useToast';
import { ApiError } from '../api/core/ApiError';
import { useTranslation } from 'react-i18next';
import { getSelectedTenant } from '../config/environment';
import { alreadyShownAsToast } from '../utils/errorToastGuard';

export const useApiErrorHandler = () => {
  const { showToast } = useToast();
  const { t } = useTranslation(['common']);
  const userProfile = useStore((state) => state.userProfile);

  const handleError = async (error: any, context?: string) => {
    // Prevent duplicate toasts when the same (re-thrown) error has already been shown as a toast
    // by another level (for example an inner FormComponent through useErrorHandler).
    if (alreadyShownAsToast(error)) return;

    // 403 (permission): do not show a generic toast - we show a specific message
    const isPermissionDenied = error instanceof ApiError && error.status === 403;

    // Extend the context with user information
    const selectedTenant = getSelectedTenant();
    const errorContext = {
      action: context || 'operation',
      userId: userProfile?.username,
      tenantId: selectedTenant?.uuid || selectedTenant?.id,
    };

    try {
      // Lazy import to avoid TDZ problems
      const [{ errorHandler }, { ErrorCategory }] = await Promise.all([
        import('../services/ErrorHandler'),
        import('../services/ErrorLogger')
      ]);

      // Central error handler: always logs, shows a toast only when it is not a 403 (the permission message comes separately)
      await errorHandler.handleError(
        error,
        errorContext,
        {
          category: ErrorCategory.API,
          showToast: !isPermissionDenied,
          preventBrowserError: true,
        }
      );
    } catch (importError) {
      // Fallback when the modules cannot be loaded
      console.error('useApiErrorHandler: Failed to load error handler modules:', importError);
      console.error(`Error in ${context || 'operation'}:`, error);
      showToast('error', 'Error', error?.message || 'An unexpected error occurred.');
    }

    // On 403: show a clear permission message (instead of 'communication error with the server')
    if (isPermissionDenied) {
      handlePermissionDeniedError(error);
    }
  };

  const isAuthenticationError = (error: any): boolean => {
    return (
      error instanceof ApiError && error.status === 401 ||
      error?.status === 401 ||
      error?.response?.status === 401 ||
      error?.body?.errorCode === 'AUTH_001' ||
      error?.response?.data?.errorCode === 'AUTH_001' ||
      error?.message?.includes('Unauthorized') ||
      error?.message?.includes('User not authenticated') ||
      error?.message?.includes('Authentication failed') ||
      error?.message?.includes('User not found in request context')
    );
  };


  const handlePermissionDeniedError = (error: ApiError) => {
    // Extract permission and entity information from error body
    const errorBody = error.body;

    // An expired licence makes the planning area read-only; that is not a missing permission.
    if (errorBody?.errorCode === 'LICENSE_002') {
      showToast('warning', t('license.expiredToast.title'), t('license.expiredToast.text'));
      return;
    }
    const requiredPermission = errorBody?.data?.requiredPermission || errorBody?.requiredPermission;
    const entityName = errorBody?.data?.entityName || errorBody?.entityName;
    const defaultMessage = t('errors.permissionDenied.default') || 'You do not have permission to perform this action.';
    const message = errorBody?.message || error.message || defaultMessage;

    // Build user-friendly error message using translations
    let errorMessage = message;
    const errorTitle = t('errors.permissionDenied.title') || 'Permission Denied';
    
    if (requiredPermission && entityName) {
      // Parse permission to get action (e.g., "material:create" -> "create")
      const action = requiredPermission.split(':')[1] || requiredPermission;
      const actionKey = getActionTranslationKey(action);
      const entityKey = getEntityTranslationKey(entityName);
      
      // Try to get translated message, fallback to default
      const translatedMessage = t(`errors.permissionDenied.specific`, {
        action: t(`errors.permissionDenied.actions.${actionKey}`, { defaultValue: action }),
        entity: t(`errors.permissionDenied.entities.${entityKey}`, { defaultValue: entityName })
      });
      
      if (translatedMessage && !translatedMessage.includes('errors.permissionDenied')) {
        errorMessage = translatedMessage;
      } else {
        // Fallback to English message if translation not found
        const actionText = getActionText(action);
        errorMessage = `You do not have permission to ${actionText} ${getEntityDisplayName(entityName)}.`;
      }
    } else if (requiredPermission) {
      // If we have permission but no entity name
      const action = requiredPermission.split(':')[1] || requiredPermission;
      const actionKey = getActionTranslationKey(action);
      const translatedMessage = t(`errors.permissionDenied.actionOnly`, {
        action: t(`errors.permissionDenied.actions.${actionKey}`, { defaultValue: action })
      });
      
      if (translatedMessage && !translatedMessage.includes('errors.permissionDenied')) {
        errorMessage = translatedMessage;
      } else {
        const actionText = getActionText(action);
        errorMessage = `You do not have permission to ${actionText}.`;
      }
    }

    showToast('error', errorTitle, errorMessage);
  };

  const getActionText = (action: string): string => {
    const actionMap: Record<string, string> = {
      'view': 'view',
      'create': 'create',
      'edit': 'edit',
      'update': 'update',
      'delete': 'delete',
      'approve': 'approve',
      'release': 'release',
      'optimize': 'optimize',
      'publish': 'publish',
    };
    return actionMap[action.toLowerCase()] || action;
  };

  const getActionTranslationKey = (action: string): string => {
    return action.toLowerCase();
  };

  const getEntityDisplayName = (entityName: string): string => {
    const entityMap: Record<string, string> = {
      'material': 'materials',
      'resource': 'resources',
      'user': 'users',
      'role': 'roles',
      'routing': 'routings',
      'productionorder': 'production orders',
      'productionschedule': 'production schedules',
      'demand': 'demands',
      'delivery': 'deliveries',
      'holiday': 'holidays',
      'shiftassignment': 'shift assignments',
      'shifttemplate': 'shift templates',
      'stocklevel': 'stock levels',
      'maintenancewindow': 'maintenance windows',
      'resourcetype': 'resource types',
      'metaattribute': 'meta attributes',
      'changeoverrouting': 'changeover routings',
      'productionscheduleorder': 'production schedule orders',
      'campaignsequence': 'campaign sequences',
    };
    return entityMap[entityName.toLowerCase()] || entityName;
  };

  const getEntityTranslationKey = (entityName: string): string => {
    return entityName.toLowerCase().replace(/([A-Z])/g, '_$1').toLowerCase();
  };

  return { handleError, isAuthenticationError };
}; 